import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, Clock, Award, FileText, HelpCircle, LogOut, ChevronRight, CheckCircle, Phone, Plus, X, Download, Lock, Calendar, CreditCard, DollarSign, AlertTriangle } from 'lucide-react';
import api from '../api';
import AITutorChat from '../components/AITutorChat';
import CMARenewalBanner from '../components/CMARenewalBanner';


// Production-safe API host: same-origin in deployed builds, env var in preview.
const _ENV_BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const _ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const _isPreviewEnv = /preview\.emergentagent\.com$/.test(
  (typeof window !== 'undefined' && window.location.hostname) || ''
);
const API_HOST = _isPreviewEnv ? _ENV_BACKEND : (_ORIGIN || _ENV_BACKEND);

const StudentDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState([]);
  const [progressSummary, setProgressSummary] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [hoursLogs, setHoursLogs] = useState([]);
  const [certificate, setCertificate] = useState(null);
  const [financialStatus, setFinancialStatus] = useState(null);
  const [onlineCourses, setOnlineCourses] = useState([]);
  const [myOnlineCourses, setMyOnlineCourses] = useState([]);
  const [resumeInfo, setResumeInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('modules');
  const [showHoursModal, setShowHoursModal] = useState(false);
  const [membership, setMembership] = useState(null);     // Feb 2026: convert-to-member banner
  const [bannerDismissed, setBannerDismissed] = useState(
    () => sessionStorage.getItem('member-banner-dismissed') === '1'
  );
  const [hoursForm, setHoursForm] = useState({
    date: new Date().toISOString().split('T')[0],
    hours: '',
    activity_type: 'classroom',
    description: ''
  });

  useEffect(() => {
    fetchData();
    // Membership status drives the convert-to-member banner.
    if (user?.id) {
      fetch(`${API_HOST}/api/membership/status/${user.id}`)
        .then((r) => r.json())
        .then(setMembership)
        .catch(() => setMembership({ is_member: false }));
    }
  }, [user?.id]);

  const dismissBanner = () => {
    sessionStorage.setItem('member-banner-dismissed', '1');
    setBannerDismissed(true);
  };

  const fetchData = async () => {
    try {
      // First, fetch financial status to determine access
      const financialRes = await api.get('/student/financial-status').catch(() => ({ data: null }));
      setFinancialStatus(financialRes.data);
      
      const [modulesRes, progressRes, summaryRes, enrollmentRes, hoursRes, certRes, onlineCoursesRes, myOnlineCoursesRes, resumeRes] = await Promise.all([
        api.get('/modules'),
        api.get(`/progress/${user.id}`),
        api.get(`/progress/summary/${user.id}`),
        api.get(`/enrollments/student/${user.id}`).catch(() => ({ data: null })),
        api.get('/student/hours').catch(() => ({ data: { logs: [] } })),
        api.get(`/certificates/${user.id}`).catch(() => ({ data: null })),
        api.get('/online-courses').catch(() => ({ data: [] })),
        api.get(`/online-courses/my-courses/${user.id}`).catch(() => ({ data: [] })),
        api.get(`/online-courses/student-resume/${user.id}`).catch(() => ({ data: null })),
      ]);
      
      setModules(modulesRes.data);
      setProgress(progressRes.data);
      setProgressSummary(summaryRes.data);
      setEnrollment(enrollmentRes.data);
      setHoursLogs(hoursRes.data.logs || []);
      setCertificate(certRes.data);
      setOnlineCourses(onlineCoursesRes.data || []);
      setMyOnlineCourses(myOnlineCoursesRes.data || []);
      setResumeInfo(resumeRes.data || null);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCertificate = async () => {
    try {
      const response = await api.get(`/certificates/${user.id}/pdf`, {
        responseType: 'blob'
      });
      
      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Certificate_${user.full_name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download certificate. Please try again.');
    }
  };

  const handleSubmitHours = async (e) => {
    e.preventDefault();
    try {
      await api.post('/student/hours/submit', {
        ...hoursForm,
        hours: parseFloat(hoursForm.hours),
        date: new Date(hoursForm.date).toISOString()
      });
      alert('Hours submitted for approval!');
      setShowHoursModal(false);
      setHoursForm({
        date: new Date().toISOString().split('T')[0],
        hours: '',
        activity_type: 'classroom',
        description: ''
      });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit hours');
    }
  };

  const getModuleStatus = (moduleId) => {
    const moduleProgress = progress.find(p => p.module_id === moduleId);
    if (!moduleProgress) return 'not-started';
    if (moduleProgress.passed) return 'completed';
    return 'in-progress';
  };

  const getModuleScore = (moduleId) => {
    const moduleProgress = progress.find(p => p.module_id === moduleId);
    return moduleProgress?.test_score || null;
  };

  const getStatusBadgeClass = (status) => {
    switch(status) {
      case 'approved': return 'active';
      case 'rejected': return 'inactive';
      default: return 'pending';
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Get program display name
  const PROGRAM_DISPLAY_NAMES = {
    'CNA': 'Certified Medical Assistant (CMA) Training Program',
    'PN_TECH': 'PN Tech - Practical Nurse Tech',
    'PHLEBOTOMY': 'Phlebotomy Training',
    'EMT': 'Emergency Medical Technician (EMT) Training',
    'HOMELAND_SECURITY': 'Master Homeland Security Specialist',
    'COMBINED_HEALTHCARE': 'Combined Healthcare & Emergency Response',
    'LPN': 'Practical Nurse (PN)',
    // Feb 2026 additions
    'CYBERSECURITY': 'Cyber Security — Online',
    'FORKLIFT_OSHA': 'Forklift OSHA Certification — Online',
    'MEDICAL_BILLING_CODING': 'Medical Billing & Coding — Online',
    'DOCTOR_NATURAL_HEALTH': 'Doctor of Natural Health — Online',
    'DOCTOR_HOLISTIC_WELLNESS': 'Doctor of Holistic Wellness — Online',
    'HERBAL_HEALTH_PRACTITIONER': 'Herbal Health Practitioner Certification — Online',
    'INTEGRATIVE_WELLNESS_CONSULTANT': 'Integrative Wellness Consultant — Online',
    'DOCTOR_VITAL_HEALTH_SCIENCES': 'Doctor of Vital Health Sciences — Practitioner — Online',
  };
  const programName = PROGRAM_DISPLAY_NAMES[user.program] || user.program;

  // Payment Gate removed — all courses are $99 flat online, paid per-step via Stripe
  // inside the online-course viewer. Students can always access the portal.
  // (Previously gated by financialStatus.can_access_portal for in-person tuition.)
  if (false && financialStatus && !financialStatus.can_access_portal) {
    return (
      <div className="dashboard" data-testid="student-dashboard">
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h1>Student Portal</h1>
            <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', marginTop: '4px' }}>
              {programName}
            </p>
          </div>
          <div className="user-info">
            <span data-testid="user-name">{user.full_name}</span>
            <button onClick={onLogout} className="btn-logout" data-testid="logout-btn">
              <LogOut size={16} style={{ marginRight: '6px' }} />
              Logout
            </button>
          </div>
        </div>
        
        <div className="dashboard-content" style={{ maxWidth: '800px', margin: '0 auto' }}>
          {/* Payment Required Notice */}
          <div style={{ 
            background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', 
            border: '2px solid #3b82f6',
            borderRadius: '16px', 
            padding: '32px',
            textAlign: 'center',
            marginBottom: '24px'
          }}>
            <FileText size={48} style={{ color: '#1e40af', marginBottom: '16px' }} />
            <h2 style={{ color: '#1e3a8a', marginBottom: '12px' }}>Visit Financial Aid Department</h2>
            <p style={{ color: '#1e40af', marginBottom: '24px', fontSize: '1.1rem' }}>
              Before starting classes, please meet with the MedCare Financial Aid Department to determine your eligibility for grants and financial assistance programs.
            </p>
          </div>
          
          {/* Financial Aid Info */}
          <div className="card" style={{ padding: '24px', marginBottom: '24px' }}>
            <h3 style={{ marginBottom: '16px', color: '#1e40af', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <DollarSign size={24} />
              Financial Aid Options
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '20px' }}>
              <div style={{ textAlign: 'center', padding: '16px', background: '#f0fdf4', borderRadius: '8px' }}>
                <p style={{ fontWeight: '700', fontSize: '1rem', color: '#166534' }}>WIOA Grants</p>
                <p style={{ color: '#15803d', fontSize: '0.875rem', margin: 0 }}>Federal workforce funding</p>
              </div>
              <div style={{ textAlign: 'center', padding: '16px', background: '#fef3c7', borderRadius: '8px' }}>
                <p style={{ fontWeight: '700', fontSize: '1rem', color: '#92400e' }}>Scholarships</p>
                <p style={{ color: '#a16207', fontSize: '0.875rem', margin: 0 }}>Merit & need-based</p>
              </div>
              <div style={{ textAlign: 'center', padding: '16px', background: '#ede9fe', borderRadius: '8px' }}>
                <p style={{ fontWeight: '700', fontSize: '1rem', color: '#5b21b6' }}>Payment Plans</p>
                <p style={{ color: '#6d28d9', fontSize: '0.875rem', margin: 0 }}>Flexible financing</p>
              </div>
              <div style={{ textAlign: 'center', padding: '16px', background: '#fce7f3', borderRadius: '8px' }}>
                <p style={{ fontWeight: '700', fontSize: '1rem', color: '#9d174d' }}>Employer Sponsorship</p>
                <p style={{ color: '#be185d', fontSize: '0.875rem', margin: 0 }}>Partner programs</p>
              </div>
            </div>
            
            <div style={{ background: '#eff6ff', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#1e40af' }}>
                <strong>Program:</strong> {financialStatus.program_name}
              </p>
            </div>
          </div>
          
          {/* Contact Financial Aid */}
          <div className="card" style={{ padding: '24px' }}>
            <h3 style={{ marginBottom: '16px', color: '#166534', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Phone size={24} />
              Contact Financial Aid Department
            </h3>
            
            <div style={{ display: 'grid', gap: '12px' }}>
              <a
                href="tel:9016251201"
                data-testid="call-financial-aid-btn"
                style={{ 
                  width: '100%', 
                  padding: '16px 20px', 
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  fontSize: '1rem', 
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px',
                  textDecoration: 'none'
                }}
              >
                <Phone size={20} />
                Call (901) 625-1201
              </a>
              
              <div style={{ 
                padding: '16px', 
                background: '#f8fafc', 
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <p style={{ margin: '0 0 8px', color: '#64748b', fontSize: '0.875rem' }}>Office Hours</p>
                <p style={{ margin: 0, fontWeight: '600', color: '#1e3a5f' }}>Monday - Friday: 9:00 AM - 5:00 PM</p>
              </div>
              
              <div style={{ 
                padding: '16px', 
                background: '#f0fdf4', 
                borderRadius: '8px',
                textAlign: 'center',
                border: '1px solid #bbf7d0'
              }}>
                <p style={{ margin: '0 0 4px', fontWeight: '600', color: '#166534' }}>Visit Us</p>
                <p style={{ margin: 0, color: '#15803d', fontSize: '0.875rem' }}>3150 E. Shelby Dr, Memphis, TN 38118</p>
              </div>
            </div>
          </div>
          
          {/* Online Courses Section - Always Available */}
          <div style={{ 
            background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', 
            border: '2px solid #059669',
            borderRadius: '16px', 
            padding: '24px',
            marginTop: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <span style={{ fontSize: '32px' }}>🎓</span>
              <div>
                <h3 style={{ margin: 0, color: '#166534' }}>Online Preparatory Courses</h3>
                <p style={{ margin: '4px 0 0', color: '#15803d', fontSize: '0.875rem' }}>
                  Start learning today! No financial aid required.
                </p>
              </div>
            </div>
            <p style={{ color: '#166534', marginBottom: '16px' }}>
              Our self-paced online courses are available immediately. $99 enrollment per course (most programs also include a $99 mid-term and $99 final exam) with instant access to reading modules, quizzes, and certificates.
            </p>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <Link
                to="/online-courses"
                style={{
                  padding: '12px 24px',
                  background: '#059669',
                  color: 'white',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                data-testid="browse-online-courses-btn"
              >
                Browse Online Courses
                <ChevronRight size={18} />
              </Link>
              {myOnlineCourses.length > 0 && (
                <Link
                  to="/online-courses"
                  style={{
                    padding: '12px 24px',
                    background: 'white',
                    color: '#059669',
                    border: '2px solid #059669',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: '600',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  data-testid="my-online-courses-btn"
                >
                  My Courses ({myOnlineCourses.length})
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate approved hours
  const approvedHours = hoursLogs.filter(l => l.status === 'approved').reduce((sum, l) => sum + l.hours, 0);
  const pendingHours = hoursLogs.filter(l => l.status === 'pending').reduce((sum, l) => sum + l.hours, 0);

  return (
    <div className="dashboard" data-testid="student-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h1>Student Portal</h1>
          <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', marginTop: '4px' }}>
            {programName}
          </p>
        </div>
        <div className="user-info">
          <span data-testid="user-name">{user.full_name}</span>
          <button onClick={onLogout} className="btn-logout" data-testid="logout-btn">
            <LogOut size={16} style={{ marginRight: '6px' }} />
            Logout
          </button>
        </div>
      </div>

      {/* Quick links to Association · Disclaimers · Refund Policy */}
      <div data-testid="student-portal-quick-links" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', padding: '12px 24px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: '0.85rem' }}>
        <Link to="/association" data-testid="quick-link-association" style={{ color: '#7c3aed', fontWeight: 600, textDecoration: 'none' }}>
          🎓 Join Association · $9.99/mo
        </Link>
        <span style={{ color: '#cbd5e1' }}>·</span>
        <Link to="/membership/dashboard" data-testid="quick-link-member-dashboard" style={{ color: '#0d9488', fontWeight: 600, textDecoration: 'none' }}>
          Member Dashboard
        </Link>
        <span style={{ color: '#cbd5e1' }}>·</span>
        <Link to="/online-courses" data-testid="quick-link-online-courses" style={{ color: '#0d9488', fontWeight: 600, textDecoration: 'none' }}>
          Browse Online Courses
        </Link>
        <span style={{ color: '#cbd5e1' }}>·</span>
        <Link to="/pricing" data-testid="quick-link-pricing" style={{ color: '#1e40af', fontWeight: 600, textDecoration: 'none' }}>
          Tuition &amp; Pricing
        </Link>
        <span style={{ color: '#cbd5e1' }}>·</span>
        <Link to="/disclaimers" data-testid="quick-link-disclaimers" style={{ color: '#b45309', fontWeight: 600, textDecoration: 'none' }}>
          Disclaimers
        </Link>
        <span style={{ color: '#cbd5e1' }}>·</span>
        <Link to="/refund-policy" data-testid="quick-link-refund" style={{ color: '#dc2626', fontWeight: 600, textDecoration: 'none' }}>
          Refund Policy
        </Link>
      </div>
      
      <div className="dashboard-content">
        {/* ============================================================
            "Get Started" hero — Resume CTA + 4-step checklist + quick-enroll
            shortcut. Replaces the old silent "land on dashboard" experience
            so the student always knows exactly what to do next.
        ============================================================== */}
        <div
          data-testid="get-started-hero"
          style={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #3b82f6 100%)',
            color: 'white',
            borderRadius: '18px',
            padding: '24px 28px',
            marginBottom: '20px',
            boxShadow: '0 10px 30px -10px rgba(30,64,175,0.45)',
          }}
        >
          {/* Top row: greeting + resume CTA */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={{ fontSize: '0.78rem', letterSpacing: 1.2, textTransform: 'uppercase', opacity: 0.85 }}>
                Welcome back
              </div>
              <h2 style={{ margin: '4px 0 0', fontSize: '1.7rem', fontWeight: 800 }}>
                {user.full_name?.split(' ')[0] || 'Student'}
              </h2>
              {resumeInfo?.has_active_course ? (
                <p style={{ margin: '6px 0 0', opacity: 0.92, fontSize: '0.95rem' }}>
                  {resumeInfo.completed
                    ? `You finished ${resumeInfo.course_title}.`
                    : resumeInfo.started
                      ? `Pick up where you left off — Module ${resumeInfo.next_module_number} of ${resumeInfo.total_modules} (${resumeInfo.progress_pct}% complete).`
                      : `Your seat is ready — start Module 1 of ${resumeInfo.total_modules}.`}
                </p>
              ) : (
                <p style={{ margin: '6px 0 0', opacity: 0.92, fontSize: '0.95rem' }}>
                  Choose a program below to begin your training in minutes.
                </p>
              )}
            </div>
            {resumeInfo?.has_active_course && !resumeInfo.completed && (
              <Link
                to={resumeInfo.resume_url || `/online-course/${resumeInfo.course_id}`}
                data-testid="resume-last-module-btn"
                style={{
                  background: '#fde68a',
                  color: '#1e3a8a',
                  padding: '14px 24px',
                  borderRadius: 999,
                  fontWeight: 800,
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                  boxShadow: '0 6px 18px -6px rgba(0,0,0,0.4)',
                  fontSize: '0.98rem',
                }}
              >
                {resumeInfo.started ? 'Resume' : 'Start'} Module {resumeInfo.next_module_number || 1}
                <ChevronRight size={18} />
              </Link>
            )}
          </div>

          {/* Progress bar inside hero — only when actively enrolled */}
          {resumeInfo?.has_active_course && (
            <div style={{ marginTop: 16 }}>
              <div style={{ height: 8, background: 'rgba(255,255,255,0.18)', borderRadius: 999, overflow: 'hidden' }}>
                <div
                  data-testid="hero-progress-bar"
                  style={{
                    height: '100%',
                    width: `${resumeInfo.progress_pct || 0}%`,
                    background: 'linear-gradient(90deg, #34d399, #fde68a)',
                    transition: 'width 0.4s ease',
                  }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: '0.78rem', opacity: 0.85 }}>
                <span>{resumeInfo.course_title}</span>
                <span>{resumeInfo.modules_completed} / {resumeInfo.total_modules} modules · {resumeInfo.progress_pct}%</span>
              </div>
            </div>
          )}

          {/* Get-Started checklist — 4 mini steps */}
          <div data-testid="get-started-checklist" style={{ marginTop: 18, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            {(() => {
              const steps = [
                { key: 'account', label: 'Account created', done: true, action: null },
                { key: 'agreement', label: 'Sign apprenticeship agreement', done: !!user.agreement_signed, action: '/apprenticeship-agreement' },
                { key: 'enroll', label: 'Enroll in a course', done: (myOnlineCourses || []).length > 0, action: '/online-courses' },
                { key: 'start', label: 'Start Module 1', done: !!resumeInfo?.started, action: resumeInfo?.has_active_course ? (resumeInfo.resume_url || `/online-course/${resumeInfo.course_id}`) : '/online-courses' },
              ];
              const StepIcon = ({ done }) => done
                ? <CheckCircle size={16} color="#34d399" />
                : <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.55)' }} />;
              return steps.map((s) => (
                <Link
                  key={s.key}
                  to={s.action || '#'}
                  data-testid={`gs-step-${s.key}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 12px',
                    background: s.done ? 'rgba(52,211,153,0.18)' : 'rgba(255,255,255,0.10)',
                    border: `1px solid ${s.done ? 'rgba(52,211,153,0.55)' : 'rgba(255,255,255,0.18)'}`,
                    borderRadius: 10,
                    textDecoration: 'none',
                    color: 'white',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    cursor: s.action ? 'pointer' : 'default',
                    pointerEvents: s.action ? 'auto' : 'none',
                    opacity: s.done && s.key !== 'start' ? 0.85 : 1,
                  }}
                >
                  <StepIcon done={s.done} />
                  <span style={{ flex: 1 }}>{s.label}</span>
                  {!s.done && s.action && <ChevronRight size={14} style={{ opacity: 0.7 }} />}
                </Link>
              ));
            })()}
          </div>

          {/* Inline quick-enroll catalog — only if no enrollments yet */}
          {(myOnlineCourses || []).length === 0 && onlineCourses.length > 0 && (
            <div data-testid="inline-enroll-catalog" style={{ marginTop: 18, padding: 16, background: 'rgba(255,255,255,0.08)', borderRadius: 12 }}>
              <div style={{ fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: 1, opacity: 0.85, marginBottom: 10 }}>
                Popular programs · One click to enroll
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                {onlineCourses.slice(0, 6).map((c) => (
                  <Link
                    key={c.id}
                    to={`/online-courses?focus=${c.id}`}
                    data-testid={`inline-enroll-card-${c.id}`}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      padding: '12px 14px',
                      background: 'rgba(255,255,255,0.10)',
                      borderRadius: 10,
                      textDecoration: 'none',
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.14)',
                    }}
                  >
                    <span style={{ fontWeight: 700, fontSize: '0.92rem' }}>{c.short_title || c.title}</span>
                    <span style={{ fontSize: '0.74rem', opacity: 0.85 }}>{c.duration || ''} · ${c.price || 99}</span>
                  </Link>
                ))}
              </div>
              <Link
                to="/online-courses"
                data-testid="inline-browse-all-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12, color: '#fde68a', fontWeight: 700, textDecoration: 'none', fontSize: '0.88rem' }}
              >
                Browse all programs <ChevronRight size={14} />
              </Link>
            </div>
          )}
        </div>

        {/* CMA Recertification banner — only renders when student holds a CMA cert that is due_soon or expired */}
        <CMARenewalBanner />

        {/* Progress Overview */}
        {progressSummary && (
          <div className="progress-section" data-testid="progress-section">
            <div className="progress-header">
              <div>
                <h3>Your Training Progress</h3>
                <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', marginTop: '4px' }}>
                  {progressSummary.videos_watched || 0} of {progressSummary.total_modules} videos watched
                  {progressSummary.quizzes_passed > 0 && ` • ${progressSummary.quizzes_passed} quizzes passed`}
                </p>
              </div>
              <div className="progress-percentage" data-testid="progress-percentage">
                {progressSummary.progress_percentage}%
              </div>
            </div>
            <div className="progress-bar-container">
              <div 
                className="progress-bar-fill" 
                style={{ width: `${progressSummary.progress_percentage}%` }}
              />
            </div>
            
            {/* Video Hours Progress */}
            <div style={{ 
              marginTop: '16px', 
              padding: '16px', 
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', 
              borderRadius: '8px',
              border: '1px solid #bae6fd'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <div style={{ fontWeight: '600', color: 'var(--primary-blue)', fontSize: '1.1rem' }}>
                    {progressSummary.total_watch_time_hours || 0} / {progressSummary.total_expected_hours || 0} hours
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                    Online Training Hours Completed
                  </div>
                </div>
                <div style={{ 
                  background: progressSummary.progress_percentage >= 100 ? '#dcfce7' : '#fef3c7',
                  color: progressSummary.progress_percentage >= 100 ? '#166534' : '#92400e',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontWeight: '600',
                  fontSize: '0.875rem'
                }}>
                  {progressSummary.progress_percentage >= 100 ? '✓ Videos Complete' : `${progressSummary.total_watch_time_minutes || 0} minutes logged`}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Progress Dashboard Widget */}
        {progressSummary && (
          <div style={{ 
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '32px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
            border: '1px solid #e2e8f0'
          }} data-testid="progress-widget">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
              <h3 style={{ margin: 0, color: '#1e3a5f', fontSize: '1.25rem' }}>Your Learning Journey</h3>
              <div style={{ 
                background: progressSummary.progress_percentage >= 100 ? '#dcfce7' : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: progressSummary.progress_percentage >= 100 ? '#166534' : 'white',
                padding: '10px 20px',
                borderRadius: '24px',
                fontWeight: '700',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                {progressSummary.progress_percentage >= 100 ? (
                  <>
                    <CheckCircle size={18} />
                    Course Complete!
                  </>
                ) : (
                  <>
                    <Clock size={18} />
                    {100 - progressSummary.progress_percentage}% to go
                  </>
                )}
              </div>
            </div>
            
            {/* Progress Milestones */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '16px',
              marginBottom: '24px'
            }}>
              {/* Milestone 1: Started */}
              <div style={{ 
                background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
                border: '1px solid #a7f3d0'
              }}>
                <CheckCircle size={32} color="#22c55e" style={{ marginBottom: '8px' }} />
                <div style={{ fontWeight: '600', color: '#166534' }}>Enrolled</div>
                <div style={{ fontSize: '0.75rem', color: '#15803d' }}>Step 1 Complete</div>
              </div>
              
              {/* Milestone 2: 25% */}
              <div style={{ 
                background: progressSummary.progress_percentage >= 25 
                  ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)'
                  : '#f8fafc',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
                border: progressSummary.progress_percentage >= 25 ? '1px solid #a7f3d0' : '1px solid #e2e8f0'
              }}>
                {progressSummary.progress_percentage >= 25 ? (
                  <CheckCircle size={32} color="#22c55e" style={{ marginBottom: '8px' }} />
                ) : (
                  <div style={{ 
                    width: '32px', 
                    height: '32px', 
                    borderRadius: '50%', 
                    border: '3px solid #cbd5e1',
                    margin: '0 auto 8px'
                  }} />
                )}
                <div style={{ fontWeight: '600', color: progressSummary.progress_percentage >= 25 ? '#166534' : '#64748b' }}>
                  25% Complete
                </div>
                <div style={{ fontSize: '0.75rem', color: progressSummary.progress_percentage >= 25 ? '#15803d' : '#94a3b8' }}>
                  {Math.ceil(progressSummary.total_modules * 0.25)} videos watched
                </div>
              </div>
              
              {/* Milestone 3: 50% */}
              <div style={{ 
                background: progressSummary.progress_percentage >= 50 
                  ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)'
                  : '#f8fafc',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
                border: progressSummary.progress_percentage >= 50 ? '1px solid #a7f3d0' : '1px solid #e2e8f0'
              }}>
                {progressSummary.progress_percentage >= 50 ? (
                  <CheckCircle size={32} color="#22c55e" style={{ marginBottom: '8px' }} />
                ) : (
                  <div style={{ 
                    width: '32px', 
                    height: '32px', 
                    borderRadius: '50%', 
                    border: '3px solid #cbd5e1',
                    margin: '0 auto 8px'
                  }} />
                )}
                <div style={{ fontWeight: '600', color: progressSummary.progress_percentage >= 50 ? '#166534' : '#64748b' }}>
                  Halfway There!
                </div>
                <div style={{ fontSize: '0.75rem', color: progressSummary.progress_percentage >= 50 ? '#15803d' : '#94a3b8' }}>
                  50% complete
                </div>
              </div>
              
              {/* Milestone 4: 100% */}
              <div style={{ 
                background: progressSummary.progress_percentage >= 100 
                  ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
                  : '#f8fafc',
                borderRadius: '12px',
                padding: '16px',
                textAlign: 'center',
                border: progressSummary.progress_percentage >= 100 ? '1px solid #fcd34d' : '1px solid #e2e8f0'
              }}>
                {progressSummary.progress_percentage >= 100 ? (
                  <Award size={32} color="#d97706" style={{ marginBottom: '8px' }} />
                ) : (
                  <div style={{ 
                    width: '32px', 
                    height: '32px', 
                    borderRadius: '50%', 
                    border: '3px solid #cbd5e1',
                    margin: '0 auto 8px'
                  }} />
                )}
                <div style={{ fontWeight: '600', color: progressSummary.progress_percentage >= 100 ? '#92400e' : '#64748b' }}>
                  Certificate Ready
                </div>
                <div style={{ fontSize: '0.75rem', color: progressSummary.progress_percentage >= 100 ? '#b45309' : '#94a3b8' }}>
                  100% complete
                </div>
              </div>
            </div>
            
            {/* Motivational Message */}
            <div style={{ 
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              borderRadius: '12px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              border: '1px solid #93c5fd'
            }}>
              <div style={{ fontSize: '2rem' }}>
                {progressSummary.progress_percentage >= 100 ? '🎉' : 
                 progressSummary.progress_percentage >= 75 ? '🔥' :
                 progressSummary.progress_percentage >= 50 ? '💪' :
                 progressSummary.progress_percentage >= 25 ? '📚' : '🚀'}
              </div>
              <div>
                <div style={{ fontWeight: '600', color: '#1e40af', marginBottom: '4px' }}>
                  {progressSummary.progress_percentage >= 100 ? 'Congratulations! You did it!' : 
                   progressSummary.progress_percentage >= 75 ? "You're almost there!" :
                   progressSummary.progress_percentage >= 50 ? 'Great progress! Keep it up!' :
                   progressSummary.progress_percentage >= 25 ? "You're making progress!" : 'Welcome to your learning journey!'}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#3b82f6' }}>
                  {progressSummary.progress_percentage >= 100 
                    ? 'Your certificate is ready for download.' 
                    : `${progressSummary.total_modules - progressSummary.videos_watched} videos remaining to complete your training.`}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="stats-grid" style={{ marginBottom: '32px' }}>
          <div className="stat-card" style={{ borderLeft: '4px solid var(--success)' }}>
            <div className="stat-value" style={{ color: 'var(--success)' }}>{progressSummary?.videos_watched || 0}</div>
            <div className="stat-label">Videos Watched</div>
            <div className="stat-change" style={{ color: 'var(--gray-500)', fontSize: '0.75rem' }}>
              of {progressSummary?.total_modules || 0} total
            </div>
          </div>
          <div className="stat-card" style={{ borderLeft: '4px solid var(--primary-blue)' }}>
            <div className="stat-value">{progressSummary?.total_watch_time_hours || 0} hrs</div>
            <div className="stat-label">Training Hours</div>
            <div className="stat-change" style={{ color: 'var(--gray-500)', fontSize: '0.75rem' }}>
              Online video credit
            </div>
          </div>
          <div className="stat-card" style={{ borderLeft: '4px solid var(--accent-blue)' }}>
            <div className="stat-value">{progressSummary?.quizzes_passed || 0}</div>
            <div className="stat-label">Quizzes Passed</div>
          </div>
          <div className="stat-card" style={{ borderLeft: progressSummary?.progress_percentage === 100 ? '4px solid var(--success)' : '4px solid var(--warning)' }}>
            <div className="stat-value" style={{ color: progressSummary?.progress_percentage === 100 ? 'var(--success)' : 'var(--warning)' }}>
              {progressSummary?.progress_percentage === 100 ? '✓' : 'In Progress'}
            </div>
            <div className="stat-label">Status</div>
          </div>
        </div>

        {/* Certificate Banner */}
        {certificate && (
          <div className="alert alert-success" style={{ marginBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Award size={24} />
                <div>
                  <strong>Congratulations!</strong> You&apos;ve completed all training modules.
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem' }}>Certificate #{certificate.certificate_number}</p>
                </div>
              </div>
              <button 
                onClick={handleDownloadCertificate}
                className="btn btn-success"
                data-testid="download-certificate-btn"
              >
                <Download size={16} style={{ marginRight: '8px' }} />
                Download Certificate
              </button>
            </div>
          </div>
        )}

        {/* Completion Banner (no certificate yet) */}
        {!certificate && progressSummary && progressSummary.completed_modules === progressSummary.total_modules && progressSummary.total_modules > 0 && (
          <div className="alert alert-success" style={{ marginBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Award size={24} />
              <div>
                <strong>Congratulations!</strong> You&apos;ve completed all training modules. Your certificate is being processed.
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="dashboard-tabs">
          <button 
            className={`dashboard-tab ${activeTab === 'modules' ? 'active' : ''}`}
            onClick={() => setActiveTab('modules')}
            data-testid="modules-tab"
          >
            <BookOpen size={16} style={{ marginRight: '8px' }} />
            Training Modules
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'hours' ? 'active' : ''}`}
            onClick={() => setActiveTab('hours')}
            data-testid="hours-tab"
          >
            <Clock size={16} style={{ marginRight: '8px' }} />
            My Hours
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'accounting' ? 'active' : ''}`}
            onClick={() => setActiveTab('accounting')}
            data-testid="accounting-tab"
          >
            <DollarSign size={16} style={{ marginRight: '8px' }} />
            My Account
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'online-courses' ? 'active' : ''}`}
            onClick={() => setActiveTab('online-courses')}
            data-testid="online-courses-tab"
            style={{ 
              background: activeTab === 'online-courses' ? '#059669' : undefined,
              color: activeTab === 'online-courses' ? 'white' : undefined
            }}
          >
            🎓 Online Courses
            {myOnlineCourses.length > 0 && (
              <span style={{
                background: '#22c55e',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: '700',
                padding: '2px 8px',
                borderRadius: '10px',
                marginLeft: '8px'
              }}>{myOnlineCourses.length}</span>
            )}
          </button>
          <button 
            className={`dashboard-tab ${activeTab === 'resources' ? 'active' : ''}`}
            onClick={() => setActiveTab('resources')}
            data-testid="resources-tab"
          >
            <HelpCircle size={16} style={{ marginRight: '8px' }} />
            Resources
          </button>
        </div>

        {/* Accounting Tab */}
        {activeTab === 'accounting' && financialStatus && (
          <div className="card" style={{ padding: '24px' }}>
            <h2 style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <DollarSign size={24} />
              Account Status
            </h2>
            
            {/* Program Info */}
            <div style={{ 
              padding: '20px', 
              background: '#eff6ff', 
              borderRadius: '12px', 
              border: '2px solid #bfdbfe',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '4px' }}>Enrolled Program</p>
              <p style={{ fontWeight: '700', fontSize: '1.25rem', color: '#1e40af' }}>{financialStatus.program_name}</p>
            </div>
            
            {/* Payment Status */}
            <div style={{ 
              padding: '16px', 
              borderRadius: '8px', 
              marginBottom: '24px',
              background: financialStatus.payment_status === 'paid' ? '#f0fdf4' : 
                         financialStatus.payment_status === 'financed' ? '#fce7f3' :
                         financialStatus.payment_status === 'payment_plan' ? '#eff6ff' : '#fef3c7',
              border: `1px solid ${
                financialStatus.payment_status === 'paid' ? '#bbf7d0' : 
                financialStatus.payment_status === 'financed' ? '#fbcfe8' :
                financialStatus.payment_status === 'payment_plan' ? '#bfdbfe' : '#fde68a'
              }`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ fontWeight: '600', marginBottom: '4px' }}>Payment Status</p>
                  <p style={{ color: '#64748b', fontSize: '0.875rem' }}>{financialStatus.status_message}</p>
                </div>
                <div style={{ 
                  padding: '8px 16px', 
                  borderRadius: '20px', 
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  background: financialStatus.payment_status === 'paid' ? '#dcfce7' : 
                             financialStatus.payment_status === 'financed' ? '#fce7f3' : '#fef3c7',
                  color: financialStatus.payment_status === 'paid' ? '#166534' : 
                         financialStatus.payment_status === 'financed' ? '#9d174d' : '#92400e'
                }}>
                  {financialStatus.payment_status === 'paid' ? '✓ Paid' : 
                   financialStatus.payment_status === 'financed' ? '✓ Klarna' :
                   financialStatus.payment_status === 'payment_plan' ? '✓ Plan Active' : 'Pending'}
                </div>
              </div>
            </div>
            
            {/* Admin-issued Waivers — surface every fee credit so the student sees the cost was forgiven */}
            {financialStatus.online_waivers && financialStatus.online_waivers.length > 0 && (
              <div style={{ marginBottom: '16px' }} data-testid="online-waivers-section">
                <h3 style={{ marginBottom: '12px' }}>Administrator Waivers</h3>
                <div style={{ border: '1px solid #bbf7d0', borderRadius: '8px', overflow: 'hidden', background: '#f0fdf4' }}>
                  {financialStatus.online_waivers.map((w, i) => (
                    <div
                      key={`${w.course_id}-${w.fee_type}-${i}`}
                      data-testid={`waiver-${w.course_id}-${w.fee_type}`}
                      style={{
                        padding: '12px 16px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottom: i < financialStatus.online_waivers.length - 1 ? '1px solid #bbf7d0' : 'none',
                      }}
                    >
                      <div>
                        <p style={{ fontWeight: '500', marginBottom: '2px' }}>
                          {w.course_title || w.course_id}
                          <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 999, background: '#bbf7d0', color: '#166534', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' }}>
                            {w.fee_type === 'midterm' ? 'Mid-term' : w.fee_type === 'final' ? 'Final exam' : 'Enrollment'} waived
                          </span>
                        </p>
                        {w.reason && (
                          <p style={{ fontSize: '0.75rem', color: '#15803d' }}>Reason: {w.reason}</p>
                        )}
                      </div>
                      <p style={{ fontWeight: '700', color: '#16a34a' }}>−${(w.amount || 0).toFixed(2)}</p>
                    </div>
                  ))}
                  <div style={{ padding: '10px 16px', background: '#dcfce7', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, color: '#166534' }}>
                    <span>Total credits applied</span>
                    <span>${(financialStatus.online_waived_amount || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Transactions */}
            {financialStatus.transactions && financialStatus.transactions.length > 0 && (
              <div>
                <h3 style={{ marginBottom: '16px' }}>Recent Transactions</h3>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                  {financialStatus.transactions.map((txn, index) => (
                    <div 
                      key={txn.id || index}
                      style={{ 
                        padding: '12px 16px', 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        borderBottom: index < financialStatus.transactions.length - 1 ? '1px solid #e5e7eb' : 'none',
                        background: index % 2 === 0 ? 'white' : '#f9fafb'
                      }}
                    >
                      <div>
                        <p style={{ fontWeight: '500', marginBottom: '2px' }}>{txn.program_name || 'Tuition Payment'}</p>
                        <p style={{ fontSize: '0.75rem', color: '#64748b' }}>
                          {txn.payment_method === 'klarna' ? 'Klarna Financing' : 'Card Payment'} • 
                          {new Date(txn.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontWeight: '600', color: '#16a34a' }}>${(txn.amount || 0).toLocaleString()}</p>
                        <p style={{ 
                          fontSize: '0.75rem', 
                          color: txn.payment_status === 'paid' ? '#16a34a' : '#f59e0b' 
                        }}>
                          {txn.payment_status === 'paid' ? '✓ Completed' : 'Pending'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Make Payment Button (if balance due) — routes to online-courses checkout */}
            {financialStatus.balance_due > 0 && (
              <div style={{ marginTop: '24px', textAlign: 'center' }}>
                <button
                  onClick={() => navigate('/online-courses')}
                  data-testid="go-to-online-courses-btn"
                  style={{ 
                    padding: '14px 32px', 
                    background: '#22c55e', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px', 
                    fontSize: '1rem', 
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Pay &amp; Access My Course
                </button>
              </div>
            )}
          </div>
        )}

        {/* Modules Tab */}
        {activeTab === 'modules' && (
          <div className="modules-section">
            {/* Convert-to-Member banner — shown only to active students who aren't yet $9.99/mo association members */}
            {membership && !membership.is_member && !bannerDismissed && (
              <div data-testid="convert-to-member-banner" style={{
                background: 'linear-gradient(135deg, #14b8a6 0%, #9333ea 100%)',
                color: 'white',
                borderRadius: 14,
                padding: '20px 24px',
                marginBottom: 24,
                display: 'flex',
                gap: 18,
                alignItems: 'center',
                flexWrap: 'wrap',
                boxShadow: '0 12px 28px -16px rgba(20,184,166,0.6)',
              }}>
                <div style={{ fontSize: '2rem', flexShrink: 0 }}>🎓</div>
                <div style={{ flex: '1 1 320px', minWidth: 280 }}>
                  <div style={{ fontWeight: 800, fontSize: '1.1rem', marginBottom: 4 }}>
                    You&apos;re enrolled — finish strong as an Association Member
                  </div>
                  <div style={{ fontSize: '0.92rem', opacity: 0.95, lineHeight: 1.5 }}>
                    Join the MedCare National Board of Nursing &amp; Vital Health Sciences Association for <strong>$9.99/mo</strong>.
                    Get <strong>10% off every future course</strong>, your <strong>CE-credit tracker</strong>, members-only NCLEX-PN bank, a downloadable <strong>digital membership card</strong>, and a <strong>verifiable LinkedIn badge</strong>.
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <Link
                    to="/association"
                    data-testid="banner-join-now-btn"
                    style={{
                      background: 'white',
                      color: '#0d9488',
                      borderRadius: 999,
                      padding: '10px 22px',
                      fontWeight: 800,
                      textDecoration: 'none',
                      fontSize: '0.95rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Join Now →
                  </Link>
                  <button
                    type="button"
                    onClick={dismissBanner}
                    data-testid="banner-dismiss-btn"
                    style={{
                      background: 'transparent',
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.45)',
                      borderRadius: 999,
                      padding: '10px 16px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: '0.88rem',
                    }}
                  >
                    Not now
                  </button>
                </div>
              </div>
            )}

            <h2>Training Modules</h2>
            
            {/* Module Access Banner */}
            {!financialStatus?.can_access_portal && (
              <div className="alert" style={{ 
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', 
                border: '1px solid #f59e0b',
                borderRadius: '12px',
                padding: '24px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px'
              }}>
                <div style={{ 
                  background: '#f59e0b', 
                  borderRadius: '50%', 
                  padding: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Lock size={24} color="white" />
                </div>
                <div>
                  <h3 style={{ margin: '0 0 4px 0', color: '#92400e' }}>Complete Payment Arrangements to Access Modules</h3>
                  <p style={{ margin: 0, color: '#a16207' }}>
                    Please complete your payment arrangements to unlock all training modules and videos.
                  </p>
                </div>
              </div>
            )}
            
            <div className="modules-grid">
              {modules.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">📚</div>
                  <h3>No modules available yet</h3>
                  <p>Training modules will appear here once you&apos;re enrolled.</p>
                </div>
              ) : (
                modules.map((module) => {
                  const status = getModuleStatus(module.id);
                  const score = getModuleScore(module.id);
                  // Video tracking deprecated — courses are reading-based now.
                  const videoWatched = false;
                  // Modules are accessible if student has made payment arrangements
                  // Modules are available once the student has signed the
                  // ETA-671 Apprenticeship Application & Tuition Agreement.
                  // No calendar-based gate; admins always have access.
                  const isLocked = !(user.agreement_signed || user.role === 'admin');
                  
                  return (
                    <div 
                      key={module.id} 
                      className="module-card" 
                      data-testid={`module-card-${module.module_number}`}
                      style={isLocked ? { opacity: 0.7 } : {}}
                    >
                      <div className="module-number">
                        {isLocked && <Lock size={12} style={{ marginRight: '4px' }} />}
                        MODULE {module.module_number}
                        {videoWatched && !isLocked && (
                          <span style={{ 
                            marginLeft: '8px', 
                            background: '#dcfce7', 
                            color: '#166534', 
                            padding: '2px 8px', 
                            borderRadius: '10px',
                            fontSize: '0.7rem',
                            fontWeight: '600'
                          }}>
                            <CheckCircle size={10} style={{ marginRight: '3px', verticalAlign: 'middle' }} />
                            WATCHED
                          </span>
                        )}
                      </div>
                      <h3>{module.title}</h3>
                      <p>{module.description}</p>
                      <div className="module-duration">
                        <Clock size={14} style={{ marginRight: '6px' }} />
                        {module.duration_minutes} minutes
                        {videoWatched && <span style={{ color: 'var(--success)', marginLeft: '8px' }}>✓ Credit Earned</span>}
                      </div>
                      
                      {!isLocked && status === 'completed' && (
                        <div style={{ marginBottom: '16px' }}>
                          <span className="module-status completed">
                            <CheckCircle size={12} style={{ marginRight: '4px' }} />
                            Quiz Passed
                          </span>
                          <div style={{ fontSize: '0.875rem', color: 'var(--success)', fontWeight: 600, marginTop: '8px' }}>
                            Score: {score}%
                          </div>
                        </div>
                      )}
                      
                      {!isLocked && status === 'in-progress' && (
                        <div style={{ marginBottom: '16px' }}>
                          <span className="module-status in-progress">In Progress</span>
                          {score !== null && (
                            <div style={{ fontSize: '0.875rem', color: 'var(--warning)', marginTop: '8px' }}>
                              Last attempt: {score}%
                            </div>
                          )}
                        </div>
                      )}
                      
                      {isLocked ? (
                        <Link
                          to="/apprenticeship-agreement"
                          className="btn-module"
                          style={{ textDecoration: 'none' }}
                          data-testid={`locked-module-${module.module_number}`}
                        >
                          <Lock size={14} style={{ marginRight: '6px' }} />
                          Complete Apprenticeship Application
                        </Link>
                      ) : status === 'not-started' ? (
                        <>
                          <span className="module-status locked" style={{ marginBottom: '16px', display: 'inline-block' }}>
                            Not Started
                          </span>
                          <Link 
                            to={`/module/${module.id}`} 
                            className="btn-module"
                            data-testid={`start-module-btn-${module.module_number}`}
                          >
                            Start Module
                            <ChevronRight size={16} style={{ marginLeft: '6px' }} />
                          </Link>
                        </>
                      ) : (
                        <Link 
                          to={`/module/${module.id}`} 
                          className="btn-module"
                          data-testid={`start-module-btn-${module.module_number}`}
                        >
                          {status === 'completed' ? 'Review Module' : 'Continue'}
                          <ChevronRight size={16} style={{ marginLeft: '6px' }} />
                        </Link>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Hours Tab */}
        {activeTab === 'hours' && (
          <div className="modules-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0 }}>My Training Hours</h2>
              <button className="btn btn-primary" onClick={() => setShowHoursModal(true)} data-testid="submit-hours-btn">
                <Plus size={16} style={{ marginRight: '8px' }} />
                Submit Hours
              </button>
            </div>

            {/* Hours Summary */}
            <div className="stats-grid" style={{ marginBottom: '24px' }}>
              <div className="stat-card" style={{ borderLeft: '4px solid var(--success)' }}>
                <div className="stat-value" style={{ color: 'var(--success)' }}>{approvedHours}</div>
                <div className="stat-label">Approved Hours</div>
              </div>
              <div className="stat-card" style={{ borderLeft: '4px solid var(--warning)' }}>
                <div className="stat-value" style={{ color: 'var(--warning)' }}>{pendingHours}</div>
                <div className="stat-label">Pending Approval</div>
              </div>
              <div className="stat-card" style={{ borderLeft: '4px solid var(--primary-blue)' }}>
                <div className="stat-value">{enrollment?.total_hours_required || (user.program === 'LPN' ? 600 : 75)}</div>
                <div className="stat-label">Hours Required</div>
              </div>
              <div className="stat-card" style={{ borderLeft: '4px solid var(--accent-blue)' }}>
                <div className="stat-value">
                  {Math.round((approvedHours / (enrollment?.total_hours_required || (user.program === 'LPN' ? 600 : 75))) * 100)}%
                </div>
                <div className="stat-label">Progress</div>
              </div>
            </div>

            {/* Hours Log Table */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Hours Log</h3>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {hoursLogs.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">⏱️</div>
                    <h3>No hours logged yet</h3>
                    <p>Click &quot;Submit Hours&quot; to log your training hours.</p>
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Hours</th>
                        <th>Activity Type</th>
                        <th>Description</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hoursLogs.map((log) => (
                        <tr key={log.id}>
                          <td>{new Date(log.date).toLocaleDateString()}</td>
                          <td>{log.hours}</td>
                          <td style={{ textTransform: 'capitalize' }}>{log.activity_type}</td>
                          <td>{log.description || '-'}</td>
                          <td>
                            <span className={`status-badge ${getStatusBadgeClass(log.status)}`}>
                              {log.status || 'pending'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Online Courses Tab */}
        {activeTab === 'online-courses' && (
          <div className="card" style={{ padding: '24px' }}>
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ margin: 0, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                🎓 Online Preparatory Courses
              </h2>
              <p style={{ color: 'var(--gray-500)', margin: 0 }}>
                Self-paced online courses to prepare for certifications. Complete video modules, pass quizzes, and earn certificates.
              </p>
            </div>

            {/* My Enrolled Courses */}
            {myOnlineCourses.length > 0 && (
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ color: 'var(--primary-navy)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Award size={20} />
                  My Enrolled Courses ({myOnlineCourses.length})
                </h3>
                <div style={{ display: 'grid', gap: '16px' }}>
                  {myOnlineCourses.map(enrollment => (
                    <div 
                      key={enrollment.id}
                      style={{
                        background: enrollment.completed_at ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' : '#f8fafc',
                        borderRadius: '12px',
                        padding: '20px',
                        border: enrollment.completed_at ? '2px solid #22c55e' : '1px solid #e2e8f0'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            <h4 style={{ margin: 0, color: 'var(--primary-navy)' }}>
                              {enrollment.course_details?.title || enrollment.course_title}
                            </h4>
                            {enrollment.completed_at && (
                              <span style={{ 
                                background: '#22c55e', 
                                color: 'white',
                                padding: '4px 12px',
                                borderRadius: '20px',
                                fontSize: '0.75rem',
                                fontWeight: 'bold'
                              }}>
                                <CheckCircle size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                                Completed
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '16px', fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                            <span>Apprentice ID: <strong>{enrollment.apprentice_id}</strong></span>
                            <span>Progress: <strong>{enrollment.modules_completed || 0}/{enrollment.total_modules} modules</strong></span>
                          </div>
                        </div>
                        <Link
                          to={`/online-course/${enrollment.course_id}`}
                          style={{
                            padding: '12px 24px',
                            background: enrollment.completed_at ? '#22c55e' : 'var(--primary-blue)',
                            color: 'white',
                            borderRadius: '8px',
                            textDecoration: 'none',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          {enrollment.completed_at ? (
                            <>
                              <Award size={18} />
                              View Certificate
                            </>
                          ) : (
                            <>
                              <ChevronRight size={18} />
                              Continue Learning
                            </>
                          )}
                        </Link>
                      </div>
                      {!enrollment.completed_at && (
                        <div style={{ marginTop: '12px' }}>
                          <div style={{ 
                            background: '#e2e8f0', 
                            borderRadius: '4px', 
                            height: '8px',
                            overflow: 'hidden'
                          }}>
                            <div style={{ 
                              width: `${(enrollment.modules_completed / enrollment.total_modules) * 100}%`,
                              height: '100%',
                              background: 'var(--primary-blue)',
                              borderRadius: '4px'
                            }} />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Available Courses */}
            <div>
              <h3 style={{ color: 'var(--primary-navy)', marginBottom: '16px' }}>
                Available Courses ($99 each)
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                {onlineCourses.map(course => {
                  const isEnrolled = myOnlineCourses.some(e => e.course_id === course.id);
                  return (
                    <div 
                      key={course.id}
                      style={{
                        background: 'white',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        overflow: 'hidden'
                      }}
                    >
                      <div style={{ 
                        background: isEnrolled ? '#059669' : 'var(--primary-navy)',
                        color: 'white',
                        padding: '16px'
                      }}>
                        <span style={{ 
                          background: 'rgba(255,255,255,0.2)',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          marginBottom: '8px',
                          display: 'inline-block'
                        }}>
                          {course.category}
                        </span>
                        <h4 style={{ margin: '8px 0 0 0', fontSize: '1rem' }}>{course.title}</h4>
                      </div>
                      <div style={{ padding: '16px' }}>
                        <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '12px' }}>
                          {course.description?.substring(0, 100)}...
                        </p>
                        <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '16px' }}>
                          <span>{course.total_modules} modules</span>
                          <span style={{ margin: '0 8px' }}>•</span>
                          <span>{course.duration}</span>
                        </div>
                        {isEnrolled ? (
                          <Link
                            to={`/online-course/${course.id}`}
                            style={{
                              display: 'block',
                              textAlign: 'center',
                              padding: '10px',
                              background: '#059669',
                              color: 'white',
                              borderRadius: '8px',
                              textDecoration: 'none',
                              fontWeight: '600'
                            }}
                          >
                            Continue Course
                          </Link>
                        ) : (
                          <Link
                            to="/online-courses"
                            style={{
                              display: 'block',
                              textAlign: 'center',
                              padding: '10px',
                              background: 'var(--primary-blue)',
                              color: 'white',
                              borderRadius: '8px',
                              textDecoration: 'none',
                              fontWeight: '600'
                            }}
                          >
                            Enroll Now - $99
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Resources Tab */}
        {activeTab === 'resources' && (
          <div className="modules-section">
            <h2>Student Resources</h2>
            
            <div className="modules-grid">
              {/* MedCare CMA Training Manual - PRIMARY */}
              <div className="module-card" style={{ border: '2px solid var(--primary-blue)', background: 'linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <BookOpen size={36} style={{ color: 'var(--primary-blue)' }} />
                  <span style={{ background: 'var(--primary-blue)', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600' }}>OFFICIAL</span>
                </div>
                <h3 style={{ color: 'var(--primary-blue)', fontSize: '1.25rem' }}>MedCare CMA Training Manual</h3>
                <p style={{ marginBottom: '16px' }}>
                  Complete 22-module training manual with clinical skills procedures, competency checklists, 
                  50-question practice test, and TN state exam preparation. Written and issued by MedCare 
                  Nursing Institute & Training Academy.
                </p>
                <div style={{ background: 'var(--off-white)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '0.875rem', marginBottom: '8px', color: 'var(--gray-700)' }}>Manual Includes:</h4>
                  <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '0.8125rem', color: 'var(--gray-600)' }}>
                    <li>All 22 CMA Training Modules</li>
                    <li>Clinical Skills Step-by-Step Procedures</li>
                    <li>Skills Competency Checklists</li>
                    <li>50-Question Practice Test with Answers</li>
                    <li>Clinical Hours Log Sheet</li>
                    <li>TN State Licensure Information</li>
                  </ul>
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <a 
                    href={`${API_HOST}/api/training-manual/download`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    style={{ textDecoration: 'none', flex: '1', minWidth: '140px', justifyContent: 'center' }}
                    data-testid="download-manual-btn"
                  >
                    <Download size={16} style={{ marginRight: '8px' }} />
                    Download PDF
                  </a>
                  <button
                    onClick={() => {
                      const url = `${API_HOST}/api/training-manual/download`;
                      const printWindow = window.open(url, '_blank');
                      if (printWindow) {
                        printWindow.onload = () => {
                          printWindow.print();
                        };
                      }
                    }}
                    className="btn btn-secondary"
                    style={{ flex: '1', minWidth: '140px', justifyContent: 'center' }}
                    data-testid="print-manual-btn"
                  >
                    <FileText size={16} style={{ marginRight: '8px' }} />
                    Print Manual
                  </button>
                </div>
              </div>
              
              {/* PN Tech Training Manual */}
              <div className="module-card" style={{ border: '2px solid #10b981', background: 'linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <BookOpen size={36} style={{ color: '#10b981' }} />
                  <span style={{ background: '#10b981', color: 'white', padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600' }}>NEW</span>
                </div>
                <h3 style={{ color: '#047857', fontSize: '1.25rem' }}>PN Tech Training Manual</h3>
                <p style={{ marginBottom: '16px' }}>
                  Complete 22-module PN foundational manual with nursing fundamentals, pharmacology basics, 
                  clinical skills procedures, and NCLEX-PN preparation guidance.
                </p>
                <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '0.875rem', marginBottom: '8px', color: '#065f46' }}>Manual Includes:</h4>
                  <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '0.8125rem', color: '#047857' }}>
                    <li>All 22 PN Tech Training Modules</li>
                    <li>Anatomy & Physiology Fundamentals</li>
                    <li>Pharmacology & Medication Administration</li>
                    <li>Clinical Skills Checklist</li>
                    <li>30-Question Practice Examination</li>
                    <li>PN (Practical Nurse) Pathway Guidance</li>
                  </ul>
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <a 
                    href={`${API_HOST}/api/pn-tech-training-manual/download`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn"
                    style={{ textDecoration: 'none', flex: '1', minWidth: '140px', justifyContent: 'center', background: '#10b981', color: 'white' }}
                    data-testid="download-pn-manual-btn"
                  >
                    <Download size={16} style={{ marginRight: '8px' }} />
                    Download PDF
                  </a>
                  <button
                    onClick={() => {
                      const url = `${API_HOST}/api/pn-tech-training-manual/download`;
                      window.open(url, '_blank');
                    }}
                    className="btn btn-secondary"
                    style={{ flex: '1', minWidth: '140px', justifyContent: 'center' }}
                    data-testid="print-pn-manual-btn"
                  >
                    <FileText size={16} style={{ marginRight: '8px' }} />
                    View Manual
                  </button>
                </div>
              </div>
              
              {/* State Exam Prep */}
              <div className="module-card">
                <Award size={32} style={{ color: 'var(--success)', marginBottom: '12px' }} />
                <h3>TN State Exam Preparation</h3>
                <p>Practice tests, study guides, and exam information for Tennessee CMA certification.</p>
                <div style={{ marginTop: '16px' }}>
                  <div style={{ background: 'var(--off-white)', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
                    <h4 style={{ fontSize: '0.9375rem', marginBottom: '8px', color: 'var(--gray-700)' }}>Exam Components:</h4>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                      <li>Written/Oral Examination (60 questions)</li>
                      <li>Clinical Skills Test (5 randomly selected skills)</li>
                      <li>Passing Score: 70% on written, all skills must pass</li>
                    </ul>
                  </div>
                  <a 
                    href="https://www.prometric.com/nurseaide/tn"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-module"
                    style={{ textDecoration: 'none', width: '100%', justifyContent: 'center' }}
                    data-testid="exam-prep-btn"
                  >
                    View TN Prometric Info
                  </a>
                </div>
              </div>
              
              {/* Clinical Requirements */}
              <div className="module-card">
                <Clock size={32} style={{ color: 'var(--warning)', marginBottom: '12px' }} />
                <h3>Clinical Requirements</h3>
                <p>Information about clinical hours, dress code, and facility requirements for Tennessee.</p>
                <div style={{ marginTop: '16px' }}>
                  <div style={{ background: 'var(--off-white)', borderRadius: '8px', padding: '16px' }}>
                    <h4 style={{ fontSize: '0.9375rem', marginBottom: '8px', color: 'var(--gray-700)' }}>Requirements:</h4>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                      <li><strong>Clinical Hours:</strong> Minimum 16 hours at approved facility</li>
                      <li><strong>Classroom Hours:</strong> Minimum 59 hours of instruction</li>
                      <li><strong>Dress Code:</strong> Scrubs, closed-toe shoes, name badge</li>
                      <li><strong>Background Check:</strong> Required before clinical placement</li>
                      <li><strong>TB Test:</strong> Current negative TB test required</li>
                      <li><strong>Immunizations:</strong> Up-to-date as per facility requirements</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* American Job Centers */}
              <div className="module-card">
                <HelpCircle size={32} style={{ color: 'var(--accent-blue)', marginBottom: '12px' }} />
                <h3>American Job Centers</h3>
                <p>Local workforce centers for career services and job-placement support.</p>
                <div style={{ marginTop: '16px' }}>
                  <div style={{ background: 'var(--off-white)', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
                    <p style={{ fontSize: '0.875rem', color: 'var(--gray-700)', margin: '0 0 8px 0' }}>
                      <strong>Memphis Career Center</strong><br />
                      3825 Ridgewood Road, Memphis, TN 38118<br />
                      (901) 543-6000
                    </p>
                    <p style={{ fontSize: '0.875rem', color: 'var(--gray-700)', margin: 0 }}>
                      <strong>Whitehaven Career Center</strong><br />
                      4851 American Way, Memphis, TN 38118<br />
                      (901) 543-6700
                    </p>
                  </div>
                  <a 
                    href="https://www.jobs4tn.gov"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-module"
                    style={{ textDecoration: 'none', width: '100%', justifyContent: 'center' }}
                  >
                    Visit Jobs4TN
                  </a>
                </div>
              </div>
              
              {/* Contact Support */}
              <div className="module-card">
                <Phone size={32} style={{ color: 'var(--primary-blue)', marginBottom: '12px' }} />
                <h3>Contact Support</h3>
                <p>Have questions? Reach out to our student support team.</p>
                <div style={{ marginTop: '16px' }}>
                  <p style={{ fontSize: '0.9375rem', marginBottom: '12px' }}>
                    <strong>Email:</strong>{' '}
                    <a href="mailto:medcare.nursing@yahoo.com">medcare.nursing@yahoo.com</a>
                  </p>
                  <p style={{ fontSize: '0.9375rem', marginBottom: '16px' }}>
                    <strong>Phone:</strong>{' '}
                    <a href="tel:9016251201">(901) 625-1201</a>
                  </p>
                  <a href="tel:9016251201" className="btn-module" style={{ textDecoration: 'none', width: '100%', justifyContent: 'center' }}>
                    <Phone size={16} style={{ marginRight: '8px' }} />
                    Call Now
                  </a>
                </div>
              </div>
              
              {/* TN Board of Nursing */}
              <div className="module-card">
                <BookOpen size={32} style={{ color: 'var(--primary-blue)', marginBottom: '12px' }} />
                <h3>TN Board of Nursing</h3>
                <p>Official Tennessee Department of Health nursing board information.</p>
                <div style={{ marginTop: '16px' }}>
                  <a 
                    href="https://www.tn.gov/health/health-program-areas/health-professional-boards/nursing-board.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-module"
                    style={{ textDecoration: 'none', width: '100%', justifyContent: 'center' }}
                  >
                    Visit TN Board of Nursing
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Submit Hours Modal */}
      {showHoursModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '32px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0 }}>Submit Training Hours</h2>
              <button onClick={() => setShowHoursModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>
            
            <p style={{ color: 'var(--gray-600)', marginBottom: '24px', fontSize: '0.9375rem' }}>
              Submit your training hours for administrator approval. Hours will be added to your record once approved.
            </p>
            
            <form onSubmit={handleSubmitHours}>
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input
                  type="date"
                  value={hoursForm.date}
                  onChange={(e) => setHoursForm(prev => ({ ...prev, date: e.target.value }))}
                  className="form-input"
                  required
                  data-testid="submit-hours-date"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Hours *</label>
                <input
                  type="number"
                  step="0.5"
                  min="0.5"
                  max="12"
                  value={hoursForm.hours}
                  onChange={(e) => setHoursForm(prev => ({ ...prev, hours: e.target.value }))}
                  className="form-input"
                  placeholder="Enter hours (e.g., 4)"
                  required
                  data-testid="submit-hours-amount"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Activity Type *</label>
                <select
                  value={hoursForm.activity_type}
                  onChange={(e) => setHoursForm(prev => ({ ...prev, activity_type: e.target.value }))}
                  className="form-input"
                  required
                  data-testid="submit-hours-activity"
                >
                  <option value="classroom">Classroom Instruction</option>
                  <option value="clinical">Clinical Training</option>
                  <option value="lab">Lab Practice</option>
                  <option value="study">Independent Study</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  value={hoursForm.description}
                  onChange={(e) => setHoursForm(prev => ({ ...prev, description: e.target.value }))}
                  className="form-input"
                  placeholder="Brief description of the activity (optional)"
                  rows={3}
                  data-testid="submit-hours-description"
                />
              </div>
              
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowHoursModal(false)} style={{ flex: 1 }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} data-testid="submit-hours-btn-confirm">
                  Submit for Approval
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <AITutorChat userId={user?.id} />
    </div>
  );
};

export default StudentDashboard;
